<!--
 * @Description: 二维码生产配置
 * @Author: 景 彡
 * @Date: 2024-10-30 14:50:42
 * @LastEditors: 景 彡
-->
<template>
  <div class="h-full">
    <c-pro-table
      ref="tableRef" align="center" v-bind="queryBind" row-key="id" :show-del-btn="false" immediate operation
    >
      <template #header>
        <a-button type="primary" @click="onEdit()">
          <c-icon-plus-circle-outlined />添加
        </a-button>

        <a-button type="primary" danger ghost @click="() => openVerificationQR = true">
          <c-icon-issues-close-outlined />核验二维码使用情况
        </a-button>
      </template>
      <template #operation="{ record }">
        <a-button type="link" @click="onEdit(record, true)">
          查看
        </a-button>
        <a-popconfirm title="确定弃用吗？" @confirm="updateBatch(record)">
          <a-button type="link">
            弃用
          </a-button>
        </a-popconfirm>
      </template>
      <template #bodyCell="{ record, column }">
        <template v-if="column.dataIndex === 'status'">
          <a-tag :color="record.status === ProductBatchStatus.批次已过期 ? 'red' : 'blue'">
            {{ ProductBatchStatus[record.status] }}
          </a-tag>
        </template>
        <template v-if="column.dataIndex === 'situation'">
          <a-tooltip :title="`已激活${record.activationCount}个，共${record.count}个`">
            <a-button type="link"> {{ record.activationCount }} / {{ record.count }}</a-button>
          </a-tooltip>
        </template>
      </template>
    </c-pro-table>

    <a-drawer v-model:open="modalState" title="添加二维码生产" destroy-on-close width="880px" placement="right">
      <c-pro-form
        ref="proFormRef"
        v-model:value="model"
        :descriptions="{ bordered: true, column: 1, labelStyle: { width: '120px' } }"
        :read-only="readonly" :fields="fields" colon
      >
        <template #productId>
          <a-select
            v-if="!readonly"
            v-model:value="model.productId"
            :options="productOptions" :loading="productLoading" :not-found-content="productLoading ? '加载中...' : undefined"
            show-search option-filter-prop="label" placeholder="请选择产品"
          />
          <span v-else>{{ productBatchInfo.name }}</span>
        </template>
      </c-pro-form>
      <template v-if="productPoint?.id">
        <a-divider>产品信息</a-divider>
        <a-descriptions bordered :label-style="{ width: '100px' }" :column="3">
          <a-descriptions-item label="积分">
            <span v-if="productPoint.randomPoints">
              {{ productPoint.minPoints }} ~ {{ productPoint.maxPoints }}
            </span>
            <span v-else> {{ productPoint.points }} </span>
          </a-descriptions-item>
          <a-descriptions-item label="红包（产品默认）">
            <span v-if="productPoint.randomCashback">
              {{ productPoint.minCashback / 100 }} ~ {{ productPoint.maxCashback / 100 }}元
            </span>
            <span v-else> {{ productPoint.cashback / 100 }}元 </span>
          </a-descriptions-item>
        </a-descriptions>
      </template>
      <template v-if="!readonly">
        <a-divider>红包策略</a-divider>
        <a-form-item label="策略" :label-col="{ style: { width: '120px' } }" colon>
          <a-radio-group v-model:value="cashbackStrategy">
            <a-radio value="product">
              使用产品默认红包
            </a-radio>
            <a-radio value="custom">
              自定义档位
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <a-alert v-if="!cashbackStrategy" class="mb16px" type="warning" message="请选择红包策略：使用产品默认红包，或为批次自定义档位" />
        <a-alert v-else-if="cashbackStrategy === 'product'" class="mb16px" type="info" :message="productDefaultCashbackText" />
        <template v-else-if="cashbackStrategy === 'custom'">
          <a-alert class="mb16px" :type="cashbackBudgetHintType" :message="cashbackBudgetHintText" />
          <a-form-item label="红包总额" :label-col="{ style: { width: '120px' } }" colon>
            <a-input-number v-model:value="cashbackBudgetYuan" :min="0" :precision="2" style="width: 200px" addon-after="元" />
            <span class="ml-8px c-gray">预计发放 {{ expectedCashback }} 元，建议总额不低于 {{ recommendedCashback }} 元</span>
          </a-form-item>
          <a-table
            :data-source="cashbackTiers" :columns="tierColumns" :pagination="false" row-key="key" size="small" bordered
          >
            <template #bodyCell="{ column, record, index }">
              <a-input-number
                v-if="column.dataIndex === 'amount'" v-model:value="record.amount" :min="0.01" :precision="2"
                style="width: 100%" addon-after="元"
              />
              <a-input-number
                v-else-if="column.dataIndex === 'probability'" v-model:value="record.probability" :min="0"
                :max="100" :precision="2" style="width: 100%" addon-after="%"
              />
              <a-button v-else type="link" danger @click="removeTier(index)">
                删除
              </a-button>
            </template>
          </a-table>
          <a-button type="dashed" block class="mt8px" @click="addTier">
            <c-icon-plus-outlined />添加档位
          </a-button>
          <div class="mt8px c-gray">
            概率合计：{{ tierProbabilityTotal }}%，须等于 100%
          </div>
        </template>
      </template>
      <template v-if="readonly">
        <a-divider>发放奖励情况</a-divider>
        <a-descriptions bordered :label-style="{ width: '100px' }" :column="2">
          <a-descriptions-item label="激活数量">
            {{ productBatchInfo.activationCount }}
          </a-descriptions-item>
          <a-descriptions-item label="发放的红包">
            <span> {{ productBatchInfo.cashback / 100 }}元 </span>
          </a-descriptions-item>
          <a-descriptions-item label="发放的积分">
            {{ productBatchInfo.points }}
          </a-descriptions-item>
          <a-descriptions-item label="抽奖次数">
            {{ productBatchInfo.sweepstakesCount }}
          </a-descriptions-item>
          <a-descriptions-item v-if="productBatchInfo.cashbackBudget > 0" label="红包总额">
            {{ (productBatchInfo.cashbackBudget / 100).toFixed(2) }}元
          </a-descriptions-item>
          <a-descriptions-item v-if="productBatchInfo.cashbackBudget > 0" label="已发放红包">
            {{ (productBatchInfo.cashbackPaid / 100).toFixed(2) }}元
          </a-descriptions-item>
          <a-descriptions-item v-if="productBatchInfo.cashbackBudget > 0" label="剩余红包">
            {{ (productBatchInfo.cashbackRemaining / 100).toFixed(2) }}元
          </a-descriptions-item>
          <a-descriptions-item label="红包策略">
            <template v-if="productBatchInfo.cashbackBudget > 0">
              自定义档位
              <a-button type="link" size="small" @click="openCashbackTiers">
                查看档位
              </a-button>
            </template>
            <template v-else>
              <div>产品默认红包</div>
              <div v-if="productBatchInfo.defaultRandomCashback">
                每包随机 {{ (productBatchInfo.defaultMinCashback / 100).toFixed(2) }} ~ {{ (productBatchInfo.defaultMaxCashback / 100).toFixed(2) }} 元
              </div>
              <div v-else>
                每包固定 {{ (productBatchInfo.defaultCashback / 100).toFixed(2) }} 元
              </div>
            </template>
          </a-descriptions-item>
        </a-descriptions>
      </template>
      <template v-if="readonly">
        <a-divider>产品激活日志</a-divider>
        <div class="h-780px">
          <c-pro-table
            ref="logTableRef"
            :columns="logColumns"
            :api="api.Products.GetProductActivationLog_PostAsync"
            :get-params="logQuery"
            align="center"
            row-key="id"
            :scroll="{ y: 590 }"
          >
            <template #header>
              <a-button type="primary" @click="logExport">
                导出
              </a-button>
            </template>
            <template #bodyCell="{ column, record }">
              <div v-if="column.dataIndex === 'userName'">
                {{ `${record.userName ? `${record.userName}-${record.userPhoneNumber}` : `${record.userPhoneNumber}`}` }}
              </div>
            </template>
          </c-pro-table>
        </div>
      </template>

      <!-- <template v-if="!readonly">
        <a-divider>二维码配置</a-divider>
        <a-descriptions bordered :label-style="{ width: '100px' }" :column="2">
          <a-descriptions-item label="背景色">
            <ColorPicker v-model:pure-color="qrConfig.bgColor" format="hex8" />
          </a-descriptions-item>
          <a-descriptions-item label="图标">
            <div v-if="qrConfig.icon" class="coverBox size-54px overflow-hidden">
              <c-image :src="qrConfig.icon" alt="avatar" :preview="true" :del-ico="true" style="height: 54px; width:54px ; object-fit:cover" @del-image="() => qrConfig.icon = ''" />
            </div>
            <a-button v-else type="dashed" block class="size-80px" @click="uploadQrIcon">
              <template #icon>
                <c-icon-plus-outlined />
              </template>
              上传
            </a-button>
          </a-descriptions-item>
          <a-descriptions-item label="颜色">
            <ColorPicker v-model:pure-color="qrConfig.color" format="hex8" />
          </a-descriptions-item>
          <a-descriptions-item label="预览结果">
            <a-qrcode
              :bg-color="qrConfig.bgColor" :color="qrConfig.color" :size="80" error-level="Q" :value="qrConfig.href"
              :icon-size="20" :icon="qrConfig.icon"
            />
          </a-descriptions-item>
        </a-descriptions>
      </template> -->
      <div class="mt16px bg-#CCEBDA p-16px">
        <div>请注意：</div>
        <div class="mt-16px pl-16px">
          <p> 1. 请将生产的二维码移交刚玉产品包装印刷单位-桂林XXX包装有限公司进行生产流程；</p>
          <p> 2. 请谨慎保存生产的二维码，仅限于刚玉系列产品包装印刷，严禁泄露。</p>
          <p> 3. 如不慎遗失生产的二维码，请马上联系刚玉公司XX经理进行注销处理。</p>
          <p> 4. 二维码生产后请核对生产数量是否正确。</p>
        </div>
        <div class="c-error">如在生产及移交过程中有任何问题，请联系刚玉公司营销中心XX经理，联系电话-13012341234 / 0773-2568256.</div>
      </div>

      <div v-show="qrCodeList.length > 0" class="fixed z--2 op-0">
        <div v-for="qrCode, index in qrCodeList" :key="index">
          <a-qrcode
            :ref="setQrCodeRef(index)" :bg-color="qrConfig.bgColor" error-level="Q" :color="qrConfig.color" :size="1080" :value="qrCode.url!"
            :icon-size="200" :icon="qrConfig.icon"
          />
        </div>
        <!-- <QrcodeVue
          v-for="item in qrCodeList" :key="item"
          class="canvasDom" :value="item" level="M" :size="500"
        /> -->
      </div>

      <template v-if="!readonly" #extra>
        <a-button style="margin-right: 8px" @click="onClose">
          取消
        </a-button>
        <a-button :disabled="!model.count" type="primary" :loading="spinner" @click="save()">创建批次</a-button>
      </template>
    </a-drawer>

    <a-modal v-model:open="tierModalOpen" title="红包档位" :footer="null" width="520px">
      <a-table :data-source="tierDetail" :columns="tierDetailColumns" :pagination="false" size="small" bordered>
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'amount'">
            {{ (record.amount / 100).toFixed(2) }} 元
          </template>
          <template v-else-if="column.dataIndex === 'probability'">
            {{ (record.probability * 100).toFixed(2) }}%
          </template>
        </template>
      </a-table>
    </a-modal>
  </div>

  <a-modal v-model:open="qrOpen" title="导出二维码" :closable="false" :mask-closable="false">
    <template #footer>
      <a-popconfirm
        title="当前导出窗口关闭则无法找回二维码，请确认下载成功后再将本窗口关闭！"
        ok-text="我已成功下载，确认关闭"
        placement="bottom"
        @confirm="onCloseQrDow"
      >
        <a-button type="primary" ghost class="w-full">
          关闭窗口
        </a-button>
      </a-popconfirm>
    </template>

    <a-button class="w-full" type="primary" @click="downloadQRCodes">
      <template #icon>
        <c-icon-download-outlined />
      </template>点击下载所有二维码链接
    </a-button>
    <a-alert class="mt16px" message="当前导出窗口关闭则无法找回二维码，请确认下载成功后再将本窗口关闭！" type="warning" />
  </a-modal>
  <Situation />
  <VerificationQR v-model:open="openVerificationQR" />
</template>

<script lang="ts" setup>
import type { CodeViewGenModel, ProductActivationLog, ProductBatch, ProductBatchPageView, ProductPoint } from '@/api/models'
import type { QRCode } from 'ant-design-vue'
import type { FormField } from 'ch2-components/types/pro-form/types'
import type { ColumnProps } from 'ch2-components/types/pro-table/types'
import * as api from '@/api/'
import { FileType, ProductBatchCashbackTierModel, ProductBatchNewModel, ProductBatchStatus, ProductBatchViewModel, QueryType } from '@/api/models'
import image from '@/assets/images/logo.png'
import { message } from 'ant-design-vue'
import * as XLSX from 'xlsx'
import Situation from './components/Situation.vue'
import 'vue3-colorpicker/style.css'

definePage({
  meta: {
    title: '二维码生产配置',
    layoutRoute: {
      meta: { layout: 'admin', title: '二维码管理', local: true, icon: 'QrcodeOutlined', order: 8 },
    },
  },
})

const columns = reactive<ColumnProps<ProductBatch>[]>([
  { dataIndex: 'count', title: '批次数量', key: 'count' },
  // { dataIndex: 'propertyName', title: '产品名称', isShow: false, search: { el: 'input' }, align: 'left', queryType: QueryType.Include },
  { dataIndex: 'created', key: 'created', title: '创建时间', align: 'center', dateFormat: true, width: '160px' },
  { dataIndex: 'createdBy', key: 'createdBy', title: '创建用户', align: 'center', width: '160px' },
  { dataIndex: ['product', 'name'], key: 'product.name', title: '关联产品', align: 'left' },
  { dataIndex: 'status', key: 'status', title: '批次状态', align: 'center', search: { el: 'enum-select', attrs: { placeholder: '请选择产品状态', enum: ProductBatchStatus } }, queryType: QueryType.Eq },
  { dataIndex: 'remark', title: '说明' },
  { dataIndex: 'situation', title: '激活情况', align: 'center' },

])

const logTableRef = useTemplateRef('logTableRef')

const logColumns = reactive<ColumnProps<ProductActivationLog>[]>([
  { dataIndex: 'productName', title: '产品名称', key: 'productName' },
  { dataIndex: 'cashback', title: '返现(元)', key: 'cashback', width: 80, bodyCell: ({ record }) => h('span', {}, record.cashback / 100) },
  { dataIndex: 'points', title: '积分', key: 'points', width: 80 },
  { dataIndex: 'sweepstakesCount', title: '抽奖次数', key: 'sweepstakesCount', width: 80 },
  { dataIndex: 'userName', title: '扫码人', key: 'userName' },
  { dataIndex: 'activationTime', title: '激活时间', key: 'activationTime', dateFormat: true, sorter: true },
])

// const { tableRef: logTableRef, queryBind: logTableQueryBind } = useTableSearch(
//   'logTableRef',
//   api.Products.GetProductActivationLog_PostAsync,
//   logColumns,
// )

const { tableRef, queryBind } = useTableSearch('tableRef', api.Products.QueryBatch_PostAsync, columns)

const openVerificationQR = ref(false)

const { productBatchInfo, modalState, onEdit, fields, spinner, save, model, readonly, onClose, updateBatch, qrCodeList, qrOpen, onCloseQrDow, qrConfig, productPoint, productOptions, productLoading, cashbackBudgetYuan, cashbackTiers, cashbackStrategy, productDefaultCashbackText, tierColumns, tierProbabilityTotal, expectedCashback, recommendedCashback, cashbackBudgetHintType, cashbackBudgetHintText, addTier, removeTier, tierModalOpen, tierDetail, tierDetailColumns, openCashbackTiers } = useEditModal()

const logQuery = computed(() => {
  return {
    productId: productBatchInfo.value.id,
  }
})

const currentProductName = ref('')

const qrCodeRefs = ref<(InstanceType<typeof QRCode>)[]>([])

function useEditModal() {
  const modalState = ref(false)

  const proFormRef = useTemplateRef('proFormRef')

  const productPoint = ref<ProductPoint | null>(null)
  const productPointLoading = ref(false)

  const productBatchInfo = ref(new ProductBatchViewModel())

  const fields = reactive<FormField[]>([
    { label: '批次数量', el: 'input-number', prop: 'count', attrs: { placeholder: '请输入批次数量，单批次最大5000个', max: 5000, style: { width: '100%' } }, required: true, formItem: {} },
    {
      label: '关联产品',
      el: 'select',
      prop: 'productId',
      attrs: {

      },
      required: true,
      formItem: { rules: [{
        required: true,
        validator: FormValidator('guidReg', '产品不能为空'),
        trigger: 'blur',
      }] },
    },
    { label: '说明', el: 'textarea', prop: 'remark', attrs: { placeholder: '请输入说明（选填）', style: { width: '100%' } }, formItem: {} },
  ])

  const model = ref(new ProductBatchNewModel())

  interface CashbackTierRow {
    key: number
    amount: number
    probability: number
  }

  const cashbackBudgetYuan = ref(0)
  const cashbackTiers = ref<CashbackTierRow[]>([])
  const cashbackStrategy = ref<'product' | 'custom'>()
  let tierKey = 0

  const productDefaultCashbackText = computed(() => {
    const point = productPoint.value
    if (!model.value.productId || model.value.productId === '00000000-0000-0000-0000-000000000000')
      return '请先选择关联产品，选择后此处显示产品默认红包设置'
    if (productPointLoading.value)
      return '正在读取产品红包设置...'
    if (!point?.id)
      return '该产品未配置积分红包，请先在「产品管理」中设置'
    if (point.randomCashback)
      return `产品默认红包：每包随机 ${point.minCashback / 100} ~ ${point.maxCashback / 100} 元`
    if (!point.cashback)
      return '该产品未配置红包'

    return `产品默认红包：每包固定 ${point.cashback / 100} 元`
  })

  const productOptions = ref<{ label: string, value: string }[]>([])
  const productLoading = ref(false)

  async function loadProductOptions() {
    if (productOptions.value.length || productLoading.value)
      return

    productLoading.value = true
    try {
      const res = await tableSearch(api.Products.Query_PostAsync, [{ limit: -1, offset: 0 }], null, null, ['id', 'name'])
      productOptions.value = ((res as any)?.items ?? []).map((v: any) => ({ label: v.name, value: v.id }))
    }
    finally {
      productLoading.value = false
    }
  }

  async function onProductChange(value: string) {
    const target = productOptions.value.find(v => v.value === value)
    currentProductName.value = target?.label ?? ''
    productPoint.value = null
    if (!value)
      return

    productPointLoading.value = true
    try {
      productPoint.value = await api.Products.GetPointAsync({ id: value })
    }
    catch {
      productPoint.value = null
    }
    finally {
      productPointLoading.value = false
    }
  }

  // 用 watch 监听选中的产品，避免依赖选择组件的事件透传
  watch(() => model.value.productId, value => onProductChange(value))

  const tierColumns = [
    { dataIndex: 'amount', title: '金额', width: 220 },
    { dataIndex: 'probability', title: '概率', width: 220 },
    { dataIndex: 'action', title: '操作', width: 100 },
  ]

  const tierProbabilityTotal = computed(() => Number(cashbackTiers.value.reduce((sum, t) => sum + (Number(t.probability) || 0), 0).toFixed(2)))

  const expectedCashback = computed(() => {
    const unit = cashbackTiers.value.reduce((sum, t) => sum + (Number(t.amount) || 0) * (Number(t.probability) || 0) / 100, 0)
    return (unit * (model.value.count || 0)).toFixed(2)
  })

  // 总额需覆盖期望发放的1.2倍，否则批次后段的大额档位会被保底预留规则压缩
  const recommendedCashback = computed(() => Math.ceil(Number(expectedCashback.value) * 1.2))

  // 仅提示不拦截：总额低于建议值时，长尾大额档位的实际概率会低于配置
  const cashbackBudgetHintType = computed(() => (Number(cashbackBudgetYuan.value) < Number(recommendedCashback.value) ? 'warning' : 'info'))

  const cashbackBudgetHintText = computed(() => {
    if (Number(cashbackBudgetYuan.value) < Number(recommendedCashback.value))
      return `本批次红包按下方金额档位和总额发放，将覆盖产品默认红包设置。当前总额低于建议的 ${recommendedCashback.value} 元，批次后段的大额档位会被保底规则压缩，实际概率可能低于配置（仍可创建）。`
    return '本批次红包按下方金额档位和总额发放，将覆盖产品默认红包设置。'
  })

  function addTier() {
    cashbackTiers.value.push({ key: ++tierKey, amount: 0, probability: 0 })
  }

  function removeTier(index: number) {
    cashbackTiers.value.splice(index, 1)
  }

  function resetCashbackTiers() {
    tierKey = 0
    cashbackBudgetYuan.value = 0
    cashbackTiers.value = [
      { key: ++tierKey, amount: 3, probability: 70 },
      { key: ++tierKey, amount: 5, probability: 20 },
      { key: ++tierKey, amount: 20, probability: 5 },
      { key: ++tierKey, amount: 88, probability: 5 },
    ]
  }

  function buildCashbackModel() {
    if (!cashbackStrategy.value) {
      message.error('请选择红包策略')
      return false
    }

    if (cashbackStrategy.value === 'product') {
      model.value.cashbackBudget = 0
      model.value.cashbackTiers = []
      return true
    }

    if (cashbackTiers.value.length === 0) {
      message.error('请至少设置一个红包档位')
      return false
    }

    const tiers = cashbackTiers.value.map(t => ({
      amount: Math.round((Number(t.amount) || 0) * 100),
      probability: Number(((Number(t.probability) || 0) / 100).toFixed(4)),
    }))

    if (tiers.some(t => t.amount <= 0)) {
      message.error('红包档位金额必须大于0')
      return false
    }

    if (tiers.some(t => t.probability <= 0)) {
      message.error('红包档位概率必须大于0')
      return false
    }

    if (Number(tiers.reduce((sum, t) => sum + t.probability, 0).toFixed(4)) !== 1) {
      message.error('红包档位概率合计必须等于100%')
      return false
    }

    if (!cashbackBudgetYuan.value || cashbackBudgetYuan.value <= 0) {
      message.error('请填写红包总额')
      return false
    }

    const count = model.value.count || 0
    const minAmount = Math.min(...tiers.map(t => t.amount)) / 100
    if (cashbackBudgetYuan.value < minAmount * count) {
      message.error(`红包总额不足，保证每张码都有保底红包至少需要${minAmount * count}元`)
      return false
    }

    model.value.cashbackBudget = Math.round(cashbackBudgetYuan.value * 100)
    model.value.cashbackTiers = tiers.map(t => Object.assign(new ProductBatchCashbackTierModel(), t))
    return true
  }

  const readonly = ref()

  const tierModalOpen = ref(false)
  const tierDetail = ref<ProductBatchCashbackTierModel[]>([])
  const tierDetailColumns = [
    { dataIndex: 'amount', title: '金额', width: 200 },
    { dataIndex: 'probability', title: '概率', width: 200 },
  ]

  async function openCashbackTiers() {
    tierDetail.value = await api.Products.GetProductBatchCashbackTiers_GetAsync({ productBatchId: productBatchInfo.value.id })
    tierModalOpen.value = true
  }

  const qrCodeList = ref<CodeViewGenModel[]>([])

  const qrOpen = ref(false)

  const qrConfig = ref({
    color: '#000000',
    bgColor: '#ffffff',
    icon: image,
    href: location.href,
  })

  function onEdit(data?: ProductBatchPageView, readOnly = false) {
    model.value = data ?? new ProductBatchNewModel()
    modalState.value = true
    readonly.value = readOnly
    if (data?.id) {
      api.Products.FindProductBatchViewById_GetAsync({ productBatchId: data?.id }).then((res) => {
        productBatchInfo.value = res
        nextTick(() => {
          logTableRef.value?.search()
        })
      })
    }
    else {
      cashbackStrategy.value = undefined
      resetCashbackTiers()
      loadProductOptions()
    }
  }

  const { run: save, spinner } = useLoading(async () => {
    await proFormRef.value?.validate()
    if (!buildCashbackModel())
      return
    return api.Products.AddBatch_PostAsync(model.value).then((res) => {
      // 增加编号到url中方便后续操作
      qrCodeRefs.value = []
      qrCodeList.value = res.map(v => ({ name: v.name, qR_Code_Number: v.qR_Code_Number, url: `${v.url}&code=${v.qR_Code_Number}` }))
      readonly.value = true
      qrOpen.value = true
      tableRef.value?.refresh()
      message.success('创建成功')
      // nextTick(download)
    })
  })

  function onClose() {
    modalState.value = false
  }

  async function updateBatch(record: ProductBatch) {
    await api.Products.UpdateBatch_PostAsync({ status: ProductBatchStatus.批次已过期, id: record.id })
    record.status = ProductBatchStatus.批次已过期
    message.success('更新成功')
  }

  async function onView(record: ProductBatch) {
    const res = await api.Products.GetBatchAsync({ id: record.id })
    console.log('%c [ res ]-151', 'font-size:13px; background:pink; color:#bf2c9f;', res)
  }

  function onCloseQrDow() {
    qrOpen.value = false
    qrCodeList.value = []
    onClose()
  }

  function uploadQrIcon() {
    useFileMangerModal(async (files) => {
      if (files.length)
        qrConfig.value.icon = await loadImage(joinFilePathById(files[0]!.id)) || ''
    }, { multiple: false, immediateReturn: true, menu: [FileType.图片] })
  }

  return { productBatchInfo, modalState, save, spinner, onEdit, fields, model, readonly, onClose, updateBatch, qrCodeList, onView, qrOpen, onCloseQrDow, qrConfig, uploadQrIcon, productPoint, productOptions, productLoading, onProductChange, cashbackBudgetYuan, cashbackTiers, cashbackStrategy, productDefaultCashbackText, tierColumns, tierProbabilityTotal, expectedCashback, recommendedCashback, cashbackBudgetHintType, cashbackBudgetHintText, addTier, removeTier, tierModalOpen, tierDetail, tierDetailColumns, openCashbackTiers }
}

function setQrCodeRef(index: number) {
  return (el: InstanceType<typeof QRCode>) => {
    if (el) {
      qrCodeRefs.value[index] = el // 将元素存储到 refs 数组
    }
  }
}

// function base64ToBlob(base64: string, type: string): Blob {
//   const cleanBase64 = replace(base64, /^data:image\/\w+;base64,/, '')

//   const byteCharacters = atob(cleanBase64) // 解码 Base64 字符串
//   const byteNumbers = new Uint8Array(byteCharacters.length)

//   for (let i = 0; i < byteCharacters.length; i++) {
//     byteNumbers[i] = byteCharacters.charCodeAt(i)
//   }

//   return new Blob([byteNumbers], { type })
// }

async function downloadQRCodes() {
  // const h = message.loading('数据量较大，请等待')
  // try {
  //   const zip = new JSZip()
  //   qrCodeRefs.value.forEach((qrCode: any, index) => {
  //     const base64 = qrCode.toDataURL()
  //     zip.file(`${qrCodeList.value[index]?.name}.png`, base64ToBlob(base64, 'image/png'))
  //   })

  //   const content = await zip.generateAsync({ type: 'blob' })
  //   saveAs(content, `${currentProductName.value}红包二维码${dayjs().format('YYYYMMDDHHmm')}.zip`)
  //   h()
  // }
  // catch {
  //   h()
  // }

  const h = message.loading('正在导出二维码链接，请稍候')
  try {
    // 构建数据
    const data = qrCodeList.value.map((item, index) => ({
      序号: index + 1,
      产品名称: item.name,
      二维码编号: item.qR_Code_Number,
      二维码链接: item.url,
    }))

    // 创建工作簿和工作表
    const worksheet = XLSX.utils.json_to_sheet(data)
    const workbook = XLSX.utils.book_new()

    // 设置列宽
    worksheet['!cols'] = [
      { width: 8 }, // 序号
      { width: 30 }, // 二维码名称列宽
      { width: 40 }, // 二维码名称列宽
      { width: 120 }, // 二维码链接列宽
    ]

    XLSX.utils.book_append_sheet(workbook, worksheet, '二维码数据')

    // 导出 Excel 文件
    XLSX.writeFile(workbook, `${currentProductName.value}_${dayjs().format('YYYYMMDDHHmm')}.xlsx`)
    h()
  }
  catch (error) {
    console.error('导出失败:', error)
    h()
  }
}

async function logExport() {
  useDownload(() => api.Products.ExportLogs_GetAsync({ productId: productBatchInfo.value.id }), `${dateTime(new Date(), 'YYYY-MM-DD')}-产品激活日志`)
}
</script>
